import React, { useContext, useEffect, useMemo, useState } from "react";
import Navbar from "../components/common/Navbar";
import { AuthContext } from "../context/Auth/AuthContext";
import { showError, showSuccess } from "../utils/toast";
import baseUrl from "../config/baseUrl";
import {
  getEmbedSettings,
  updateEmbedSettings,
  createEmbedPreviewToken,
  revealEmbedSecret,
} from "../api/embed";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { CircularProgress, Dialog, Switch, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WebOutlinedIcon from "@mui/icons-material/WebOutlined";
import "../styles/Users.css";
import "../styles/DocumentTable.css";

const PANELS = [
  { key: "settings", label: "Embed Settings", icon: SettingsOutlinedIcon },
  { key: "secret", label: "Signing Secret", icon: KeyOutlinedIcon },
];

const MASKED_SECRET ="••••••••••••••••••••••••••••••••";

// Administration > Embed & API (admin only). Configures the public /embed page
// that shows a client's Requested / Uploaded documents inside any external
// portal or CRM. The external system's server signs a short-lived, one-time
// launch token with the secret generated here; nothing secret is ever put in
// embed code.

const IDENTIFIERS = [
  { value: "client_id", label: "Client ID", placeholder: "CLIENT_ID", php: "$clientId", js: "clientId" },
  {
    value: "client_reference_number",
    label: "Client Reference Number",
    placeholder: "CLIENT_REFERENCE_NUMBER",
    php: "$clientReferenceNumber",
    js: "clientReferenceNumber",
  },
  { value: "case_id", label: "Case ID", placeholder: "CASE_ID", php: "$caseId", js: "caseId" },
];

// Public embed URL on the DMS API host (GET /api/embed redirects to the
// DMS web app's /embed page). Follows config/baseUrl - never localhost.
const EMBED_BASE_URL = `${baseUrl}/api`;
const EMBED_URL = `${EMBED_BASE_URL}/embed`;

// Live Preview only: loads this DMS web app's /embed page directly.
const PREVIEW_URL = `${window.location.origin}/embed`;

const INTEGRATIONS = [
  { value: "sugarcrm", label: "SugarCRM (PHP)", codeLabel: "PHP - SugarCRM" },
  { value: "node", label: "Node.js", codeLabel: "Node.js" },
  { value: "iframe", label: "HTML Iframe", codeLabel: "HTML" },
  { value: "link", label: "Button / Link", codeLabel: "Button / Link" },
];

const buildCode = (integration, id) => {
  switch (integration) {
    case "node":
      return `// Server-side only - keep the signing secret out of the browser.
const crypto = require("crypto");

const b64 = (value) => Buffer.from(value).toString("base64url");

// One-time launch token, valid for 5 minutes (HS256).
function dmsEmbedUrl(${id.js}) {
  const now = Math.floor(Date.now() / 1000);
  const header = b64(JSON.stringify({ alg: "HS256", typ: "JWT" }));
  const payload = b64(JSON.stringify({
    ${id.value}: ${id.js},
    iat: now,
    exp: now + 300,
    jti: crypto.randomBytes(16).toString("hex"),
  }));
  const signature = crypto
    .createHmac("sha256", process.env.DMS_EMBED_SECRET)
    .update(header + "." + payload)
    .digest("base64url");
  return "${EMBED_URL}?token=" + encodeURIComponent(header + "." + payload + "." + signature);
}

// Send only the URL to the browser, e.g. as the iframe src:
// <iframe src={dmsEmbedUrl("${id.placeholder}")} width="100%" height="800" style="border:0"></iframe>`;
    case "iframe":
      return `<!-- {EMBED_TOKEN}: generated by your server for every page view
     (see SugarCRM (PHP) or Node.js). Never put the signing secret in HTML. -->
<iframe
    src="${EMBED_URL}?token={EMBED_TOKEN}"
    width="100%"
    height="800px"
    style="border:0;"
    title="DMS Client Documents">
</iframe>`;
    case "link":
      return `<!-- Button / link: opens the client's documents in a new tab.
     {EMBED_TOKEN} is generated by your server when the page is rendered. -->
<a href="${EMBED_URL}?token={EMBED_TOKEN}"
   target="_blank" rel="noopener"
   style="display:inline-block;padding:8px 16px;border-radius:6px;background:#1a6b7c;color:#fff;text-decoration:none;">
    View DMS Documents
</a>`;
    case "sugarcrm":
    default:
      return `<?php
// Server-side only - keep the signing secret out of the browser.
$dmsEmbedSecret = getenv('DMS_EMBED_SECRET'); // DMS > Administration > Embed & API
${id.php} = '${id.placeholder}'; // Replace with the actual ${id.label} or a dynamic value

// One-time launch token, valid for 5 minutes (HS256).
$b64 = fn ($data) => rtrim(strtr(base64_encode($data), '+/', '-_'), '=');
$header  = $b64(json_encode(['alg' => 'HS256', 'typ' => 'JWT']));
$payload = $b64(json_encode([
    '${id.value}' => ${id.php},
    'iat' => time(),
    'exp' => time() + 300,
    'jti' => bin2hex(random_bytes(16)),
]));
$embedToken = "$header.$payload." . $b64(hash_hmac('sha256', "$header.$payload", $dmsEmbedSecret, true));
?>
<iframe
    src="${EMBED_URL}?token=<?php echo urlencode($embedToken); ?>"
    width="100%"
    height="800px"
    style="border:0;"
    title="DMS Client Documents">
</iframe>`;
  }
};

// Lightweight display-only highlighting (comments, strings, tags, variables).
const TOKEN_RE =
  /(\/\/[^\n]*|<!--[\s\S]*?-->|'[^'\n]*'|"[^"\n]*"|<\?php|\?>|<\/?[a-zA-Z]+|\$[a-zA-Z_]\w*)/g;
const tokenClass = (t) => {
  if (t.startsWith("//") || t.startsWith("<!--")) return "text-slate-400 italic";
  if (t.startsWith("'") || t.startsWith('"')) return "text-amber-300";
  if (t.startsWith("$")) return "text-sky-300";
  return "text-pink-300";
};
const HighlightedCode = ({ code }) =>
  code.split(TOKEN_RE).map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className={tokenClass(part)}>
        {part}
      </span>
    ) : (
      part
    ),
  );

const copyText = async (text, label = "Copied to clipboard") => {
  try {
    await navigator.clipboard.writeText(text);
    showSuccess(label);
  } catch (e) {
    showError("Unable to copy - select the text and copy it manually");
  }
};

// Centered popup used by "Embed Settings" and "Signing Secret": header with
// icon / title / close, controls on the left, an illustrated info panel on
// the right (stacks on small screens) and a centered action footer.
const ConfigModal = ({ open, onClose, icon: Icon, title, subtitle, aside, footer, children }) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="md"
    PaperProps={{
      sx: {
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 24px 48px -12px rgba(15, 42, 51, 0.35)",
      },
    }}
  >
    <header className="flex items-center gap-4 px-6 py-4">
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-nbl-tint text-nbl-primary">
        <Icon sx={{ fontSize: 26 }} />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-lg font-semibold leading-tight text-gray-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
      </div>
      <Tooltip title="Close" arrow>
        <button
          aria-label="Close"
          onClick={onClose}
          className="-mr-2 inline-flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </button>
      </Tooltip>
    </header>
    <div className="grid border-y border-gray-100 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <div className="px-6 py-5">{children}</div>
      <aside className="border-t border-gray-100 bg-gradient-to-b from-[#eef8f9] to-[#f6fbfc] px-6 py-5 md:border-l md:border-t-0">
        {aside}
      </aside>
    </div>
    <footer className="flex flex-wrap items-center justify-center gap-3 px-6 py-4">
      {footer}
    </footer>
  </Dialog>
);

// Small decorative illustration (browser window + round badge icon).
const ModalIllustration = ({ badgeIcon: BadgeIcon, title, text }) => (
  <div className="flex h-full flex-col justify-center">
    <div className="relative mx-auto mb-4 h-[104px] w-[150px]" aria-hidden="true">
      <span className="absolute inset-0 -m-3 rounded-full border border-dashed border-sky-200" />
      <div className="absolute left-0 top-2 h-[78px] w-[118px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
        <div className="flex h-4 items-center gap-1 bg-nbl-primary px-2">
          <span className="h-1 w-1 rounded-full bg-white" />
          <span className="h-1 w-1 rounded-full bg-white" />
          <span className="h-1 w-1 rounded-full bg-white" />
        </div>
        <div className="space-y-1.5 p-2">
          <div className="flex gap-1.5">
            <span className="h-4 w-5 rounded bg-sky-100" />
            <span className="mt-1 h-1.5 flex-1 rounded bg-gray-200" />
          </div>
          <div className="flex gap-1.5">
            <span className="h-4 w-5 rounded bg-sky-100" />
            <span className="mt-1 h-1.5 w-12 rounded bg-gray-200" />
          </div>
        </div>
      </div>
      <span className="absolute bottom-0 right-0 flex h-14 w-14 items-center justify-center rounded-full border-4 border-white bg-sky-50 text-nbl-primary shadow-md">
        <BadgeIcon sx={{ fontSize: 28 }} />
      </span>
    </div>
    <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
    <p className="mt-1 text-sm leading-relaxed text-gray-600">{text}</p>
  </div>
);

const Card = ({ icon: Icon, title, subtitle, action, onClose, children, className = "", bodyClassName = "" }) => (
  <section
    className={`rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] ${className}`}
  >
    <header className="flex items-center gap-3 px-4 py-2.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-nbl-tint text-nbl-primary">
        <Icon sx={{ fontSize: 20 }} />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-[15px] font-semibold leading-tight text-gray-900">
          {title}
        </h2>
        {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
      </div>
      {action}
      {onClose && (
        <Tooltip title="Close" arrow>
          <button
            aria-label="Close"
            onClick={onClose}
            className="-mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </Tooltip>
      )}
    </header>
    {children && <div className={`border-t border-gray-100 px-4 py-2.5 ${bodyClassName}`}>{children}</div>}
  </section>
);

const Label = ({ children }) => (
  <label className="mb-1 block text-xs font-semibold text-gray-700">{children}</label>
);

const fieldClass =
  "h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-800 focus:border-nbl-primary focus:outline-none focus:ring-1 focus:ring-nbl-primary";

const primaryButton =
  "inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md bg-nbl-primary px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-nbl-hover disabled:cursor-not-allowed disabled:opacity-60";

const modalPrimaryButton =
  "inline-flex h-11 min-w-[150px] items-center justify-center rounded-lg bg-nbl-primary px-6 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-nbl-hover disabled:cursor-not-allowed disabled:opacity-60";

const secondaryButton =
  "inline-flex h-11 min-w-[110px] items-center justify-center rounded-lg border border-gray-300 bg-white px-6 text-sm font-semibold text-gray-800 shadow-sm transition-colors hover:bg-gray-50";

// Compact controls for the Embed Code / Live Preview inputs.
const compactField =
  "h-8 w-full rounded-md border border-gray-300 bg-white px-2.5 text-[13px] text-gray-800 focus:border-nbl-primary focus:outline-none focus:ring-1 focus:ring-nbl-primary";

const compactIconButton =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-nbl-primary";

// Header utility buttons (Embed Settings / Signing Secret): outlined teal.
const utilityButton =
  "inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-md border border-nbl-primary bg-white px-3 text-[13px] font-semibold text-nbl-primary transition-colors hover:bg-nbl-tint";

const iconButton =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-nbl-primary";

const EmbedApi = () => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    secret_set: false,
    secret_rotated_at: null,
    session_minutes: 60,
  });
  const [revealedSecret, setRevealedSecret] = useState("");
  const [secretBusy, setSecretBusy] = useState(false);
  const [integration, setIntegration] = useState("sugarcrm");
  const [identifierType, setIdentifierType] = useState("client_id");
  const [previewValue, setPreviewValue] = useState("");
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  // True from setting the preview URL until the embedded page reports it has
  // finished opening (see EmbedClientDocuments), drives the loading overlay.
  const [frameLoading, setFrameLoading] = useState(false);
  const previewBusy = previewLoading || (!!previewSrc && frameLoading);

  useEffect(() => {
    if (!previewSrc) {
      setFrameLoading(false);
      return;
    }
    setFrameLoading(true);
    const onMessage = (e) => {
      if (e.origin === window.location.origin && e.data?.type === "dms-embed-ready") {
        setFrameLoading(false);
      }
    };
    window.addEventListener("message", onMessage);
    // Fallback so the overlay can never get stuck.
    const timer = setTimeout(() => setFrameLoading(false), 30000);
    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
    };
  }, [previewSrc]);
  // Which header-button popup is open: "settings", "secret" or none.
  const [openPanel, setOpenPanel] = useState(null);
  const closePanel = () => setOpenPanel(null);
  // Session lifetime when the Embed Settings popup was opened, so Cancel
  // (or X / outside click) discards unsaved edits.
  const [minutesSnapshot, setMinutesSnapshot] = useState(null);
  const openPanelFor = (key) => {
    if (key === "settings") setMinutesSnapshot(settings.session_minutes);
    setOpenPanel(key);
  };
  const cancelSettings = () => {
    if (minutesSnapshot !== null) {
      setSettings((s) => ({ ...s, session_minutes: minutesSnapshot }));
    }
    closePanel();
  };

  const load = async () => {
    const res = await getEmbedSettings({ token });
    if (res?.code === 200) {
      setSettings(res.data);
    } else {
      showError(res?.message || "Unable to load embed settings");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const identifier = IDENTIFIERS.find((i) => i.value === identifierType);
  const integrationMeta = INTEGRATIONS.find((i) => i.value === integration);
  const code = useMemo(() => buildCode(integration, identifier), [integration, identifier]);
  const save = async (extra = {}) => {
    setSaving(true);
    const res = await updateEmbedSettings({
      token,
      data: {
        enabled: settings.enabled,
        session_minutes: settings.session_minutes,
        ...extra,
      },
    });
    setSaving(false);
    if (res?.code !== 200) {
      showError(res?.message || "Unable to save embed settings");
      return false;
    }
    if (extra.regenerate_secret) {
      // Keep the new secret masked; Show / Copy Secret fetch it when needed.
      setRevealedSecret("");
      showSuccess("New signing secret generated - copy it into your server configuration");
    } else {
      showSuccess(res.message || "Embed settings saved");
    }
    load();
    return true;
  };

  // The header switch is saved right away (previously it only took effect
  // after "Save Settings", which left embeds failing with 403).
  const toggleEnabled = async (enabled) => {
    setSettings((s) => ({ ...s, enabled }));
    const ok = await save({ enabled });
    if (!ok) setSettings((s) => ({ ...s, enabled: !enabled }));
  };

  // Same save as before; the popup closes once it has been saved.
  const saveSettings = async () => {
    if (await save()) {
      setMinutesSnapshot(null);
      closePanel();
    }
  };

  const regenerateSecret = () => {
    if (
      settings.secret_set &&
      !window.confirm(
        "Generate a new secret? The current secret stops working immediately, so every system using it must be updated.",
      )
    )
      return;
    save({ regenerate_secret: true });
  };

  const loadPreview = async () => {
    if (!previewValue.trim()) {
      showError(`Enter a ${identifier.label}`);
      return;
    }
    setPreviewLoading(true);
    const res = await createEmbedPreviewToken({
      token,
      identifierType,
      identifierValue: previewValue.trim(),
    });
    setPreviewLoading(false);
    if (res?.code === 200 && res.token) {
      setPreviewSrc(`${PREVIEW_URL}#token=${encodeURIComponent(res.token)}`);
    } else {
      showError(res?.message || "Unable to create a preview");
    }
  };

  // The secret is fetched from the server only for Show / Copy and is not kept
  // in the page: hiding (or the 30s auto-hide) clears it again.
  const fetchSecret = async () => {
    setSecretBusy(true);
    const res = await revealEmbedSecret({ token });
    setSecretBusy(false);
    if (res?.code === 200 && res.secret) return res.secret;
    showError(res?.message || "Unable to read the signing secret");
    return null;
  };

  const toggleSecret = async () => {
    if (revealedSecret) {
      setRevealedSecret("");
      return;
    }
    const secret = await fetchSecret();
    if (secret) setRevealedSecret(secret);
  };

  const copySecret = async () => {
    const secret = revealedSecret || (await fetchSecret());
    if (secret) copyText(secret, "Secret copied");
  };

  useEffect(() => {
    if (!revealedSecret) return;
    const timer = setTimeout(() => setRevealedSecret(""), 30000);
    return () => clearTimeout(timer);
  }, [revealedSecret]);

  const secretSubtitle = settings.secret_set
    ? `Used only on your server to sign embed tokens${settings.secret_rotated_at ? ` - generated ${settings.secret_rotated_at}` : ""}.`
    : "Generate a secret to start embedding. The secret is only used on your server.";

  return (
    <div className="user-container" style={{ overflowY: "hidden" }}>
      <div className="navbar">
        <Navbar />
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-[#f7fbfc] px-3 pb-6 pt-2 md:px-5">
        <nav className="flex items-center gap-1 text-sm text-gray-500">
          <span>Administration</span>
          <ChevronRightIcon sx={{ fontSize: 16 }} />
          <span className="font-medium text-gray-900">Embed &amp; API</span>
        </nav>

        <section className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-nbl-primary text-white">
            <CodeOutlinedIcon sx={{ fontSize: 24 }} />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold leading-tight text-gray-900">
              Embed &amp; API
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Show a client's Requested and Uploaded documents inside SugarCRM, a
              client portal or any other application.
            </p>
          </div>
          {!loading && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {PANELS.map(({ key, label, icon: PanelIcon }) => (
                <button
                  key={key}
                  className={utilityButton}
                  aria-haspopup="dialog"
                  onClick={() => openPanelFor(key)}
                >
                  <PanelIcon sx={{ fontSize: 16 }} />
                  {label}
                </button>
              ))}
            </div>
          )}
          <Tooltip title="Turns embedding on or off immediately" arrow>
            <label className="flex shrink-0 items-center text-sm font-medium text-gray-700">
              <Switch
                checked={!!settings.enabled}
                disabled={loading || saving}
                onChange={(e) => toggleEnabled(e.target.checked)}
              />
              {settings.enabled ? "Enabled" : "Disabled"}
            </label>
          </Tooltip>
        </section>

        {loading ? (
          <p className="px-1 py-4 text-sm text-gray-500">Loading...</p>
        ) : (
          <>
            {/* Embed Settings / Signing Secret open as centered popups from the
                header buttons, so the main content below never moves. */}
            <ConfigModal
              open={openPanel === "settings"}
              onClose={cancelSettings}
              icon={SettingsOutlinedIcon}
              title="Embed Settings"
              subtitle="Set how long an embedded session stays signed in."
              aside={
                <ModalIllustration
                  badgeIcon={TimerOutlinedIcon}
                  title="Keep users signed in"
                  text="Control how long the embedded DMS session remains active inside SugarCRM or other applications."
                />
              }
              footer={
                <>
                  <button className={secondaryButton} onClick={cancelSettings}>
                    Cancel
                  </button>
                  <button className={modalPrimaryButton} disabled={saving} onClick={saveSettings}>
                    {saving ? "Saving..." : "Save Settings"}
                  </button>
                </>
              }
            >
              <Label>Session Lifetime (minutes)</Label>
              <input
                type="number"
                min={5}
                max={480}
                className={`${fieldClass} h-11 text-base`}
                value={settings.session_minutes}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, session_minutes: e.target.value }))
                }
              />
              <p className="mt-2 text-sm text-gray-500">
                How long an opened embed stays signed in (5-480). After that the
                page must be reloaded.
              </p>
            </ConfigModal>

            <ConfigModal
              open={openPanel === "secret"}
              onClose={closePanel}
              icon={KeyOutlinedIcon}
              title="Signing Secret"
              subtitle={secretSubtitle}
              aside={
                <ModalIllustration
                  badgeIcon={VerifiedUserOutlinedIcon}
                  title="Secure Integration"
                  text="This secret is used to sign embed tokens on your server for secure client access."
                />
              }
              footer={
                <>
                  <button className={secondaryButton} onClick={closePanel}>
                    Cancel
                  </button>
                  <button className={modalPrimaryButton} disabled={saving} onClick={regenerateSecret}>
                    {settings.secret_set ? "Regenerate Secret" : "Generate Secret"}
                  </button>
                </>
              }
            >
              {settings.secret_set ? (
                <>
                  <Label>Signing Secret</Label>
                  <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                    <input
                      readOnly
                      aria-label="Signing secret"
                      className={`${fieldClass} h-11 min-w-0 flex-1 bg-white font-mono ${revealedSecret ? "text-xs" : "tracking-widest"}`}
                      value={revealedSecret || MASKED_SECRET}
                    />
                    <Tooltip title={revealedSecret ? "Hide secret" : "Show secret"} arrow>
                      <button
                        className={`${iconButton} h-11 w-11`}
                        disabled={secretBusy}
                        onClick={toggleSecret}
                      >
                        {revealedSecret ? (
                          <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                        ) : (
                          <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                        )}
                      </button>
                    </Tooltip>
                    <button
                      className={`${iconButton} h-11 w-auto gap-1.5 px-4 text-sm font-semibold text-gray-800`}
                      disabled={secretBusy}
                      onClick={copySecret}
                    >
                      <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
                      Copy Secret
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-600">
                  No signing secret yet. Generate one to start embedding, then
                  copy it into your server configuration.
                </p>
              )}
              <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-gray-600">
                <WarningAmberOutlinedIcon sx={{ fontSize: 20 }} className="shrink-0 text-amber-500" />
                Store it in your server configuration (e.g. DMS_EMBED_SECRET), never
                in browser code. Regenerating it stops the current secret
                immediately, so every system using it must be updated.
              </p>
            </ConfigModal>

            <div className="grid gap-3 lg:grid-cols-2">
              <Card
                icon={CodeOutlinedIcon}
                title="Embed Code"
                subtitle="Select the integration type, then copy the code into your portal or application."
              >
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label>Integration Type</Label>
                    <select
                      className={compactField}
                      value={integration}
                      onChange={(e) => setIntegration(e.target.value)}
                    >
                      {INTEGRATIONS.map((i) => (
                        <option key={i.value} value={i.value}>
                          {i.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Identifier Type</Label>
                    <select
                      className={compactField}
                      value={identifierType}
                      onChange={(e) => {
                        setIdentifierType(e.target.value);
                        setPreviewSrc("");
                      }}
                    >
                      {IDENTIFIERS.map((i) => (
                        <option key={i.value} value={i.value}>
                          {i.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-3">
                  <Label>Embed URL</Label>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      className={`${compactField} bg-gray-50 font-mono`}
                      value={EMBED_BASE_URL}
                    />
                    <Tooltip title="Copy Embed URL" arrow>
                      <button
                        className={compactIconButton}
                        onClick={() => copyText(EMBED_BASE_URL, "Embed URL copied")}
                      >
                        <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                      </button>
                    </Tooltip>
                  </div>
                </div>

                <div className="mt-3">
                  <Label>Embed Code ({integrationMeta.codeLabel})</Label>
                  <div className="relative overflow-hidden rounded-lg border border-[#0b2027] bg-[#0f2a33] shadow-inner">
                    <button
                      className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border border-white/20 bg-white/10 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm hover:bg-white/20"
                      onClick={() => copyText(code, "Code copied")}
                    >
                      <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
                      Copy
                    </button>
                    <pre className="max-h-[520px] overflow-auto p-4 pr-20 font-mono text-xs leading-relaxed text-slate-100">
                      <code>
                        <HighlightedCode code={code} />
                      </code>
                    </pre>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500">
                    Replace {identifier.placeholder} with the actual {identifier.label}.
                    Tokens are single-use, so generate a new one for every page view.
                  </p>
                </div>
              </Card>

              <Card
                className="flex flex-col"
                bodyClassName="flex flex-1 flex-col"
                icon={VisibilityOutlinedIcon}
                title="Live Preview"
                subtitle="Preview exactly how it will look in an external system."
              >
                <Label>Enter {identifier.label}</Label>
                <div className="flex gap-2">
                  <input
                    className={compactField}
                    placeholder="Enter Client ID / Reference Number / Case ID"
                    value={previewValue}
                    onChange={(e) => setPreviewValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && loadPreview()}
                  />
                  <button
                    className={`${primaryButton} h-8 text-[13px]`}
                    disabled={previewLoading || !settings.secret_set}
                    onClick={loadPreview}
                  >
                    {previewLoading ? "Loading..." : "Load Preview"}
                  </button>
                </div>
                {(!settings.enabled || !settings.secret_set) && (
                  <p className="mt-1.5 text-xs text-amber-700">
                    {!settings.secret_set
                      ? "Generate a signing secret to load a preview."
                      : "Embedding is disabled - enable it and save to load a preview."}
                  </p>
                )}
                {/* Edge-to-edge preview area filling the rest of the card. */}
                <div className="relative -mx-4 -mb-2.5 mt-2.5 min-h-[calc(100vh-290px)] flex-1 border-t border-gray-200 bg-gray-50">
                  {previewSrc ? (
                    <iframe
                      key={previewSrc}
                      src={previewSrc}
                      title="DMS embed preview"
                      className="absolute inset-0 block h-full w-full bg-white"
                    />
                  ) : (
                    !previewBusy && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
                        <WebOutlinedIcon sx={{ fontSize: 36 }} className="text-gray-300" />
                        <p className="text-sm font-medium text-gray-600">No preview loaded</p>
                        <p className="text-xs text-gray-500">
                          Enter a {identifier.label} and click Load Preview to see the
                          client's Requested and Uploaded documents.
                        </p>
                      </div>
                    )
                  )}
                  {previewBusy && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/80 backdrop-blur-[1px]">
                      <CircularProgress size={36} thickness={4} sx={{ color: "var(--nbl-primary)" }} />
                      <p className="text-sm font-medium text-gray-600">Loading preview...</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmbedApi;
